import { Box, Skeleton } from '@mui/material';
import { getMetricConfig } from '../registry/metricsRegistry';
import { rendererMap } from '../registry/rendererMap';
import { useMetric } from '../hooks/useMetric';
import type { ContentRef, Filters, MetricDataLevel, MetricPresentation, MetricRenderer } from '../types';
import { useMetricsFrameworkContext } from '../context/MetricsFrameworkContext';
import { getMetricDisplayName } from './renderers/rendererTypes';
import { isTileEmptyStatus } from '../utils/tileFetchError';
import TileErrorCard from './TileErrorCard';

type Props = {
  id: string;
  filters?: Filters;
  span?: ContentRef['span'];
  renderer?: MetricRenderer;
  presentation?: MetricPresentation;
  /** Defaults to L1 (page tile). Section/metric L2 modals pass L2 → full_records. */
  dataLevel?: MetricDataLevel;
  /** Hide in-card display_name (e.g. when section header already shows it). */
  hideTitle?: boolean;
};

const skeletonHeight = (renderer: MetricRenderer): number => {
  switch (renderer) {
    case 'hero':
    case 'heroWithAlert':
      return 160;
    case 'dataTable':
    case 'verticalBarChart':
      return 280;
    case 'sectionTitleMetric':
      return 40;
    case 'rankedBreakdown':
      return 260;
    default:
      return 140;
  }
};

export default function Metric({
  id,
  filters: filtersProp,
  span,
  renderer: rendererOverride,
  presentation,
  dataLevel = 'L1',
  hideTitle = false,
}: Props) {
  const { filters: contextFilters } = useMetricsFrameworkContext();
  const filters = filtersProp ?? contextFilters;
  const config = getMetricConfig(id);
  const { data, isLoading, error, statusCode } = useMetric(id, filters, dataLevel);

  if (!config) {
    return null;
  }

  const renderer = rendererOverride ?? config.renderer;
  const heading = hideTitle
    ? undefined
    : (data && getMetricDisplayName(data, config)) || config.label;

  if (!isLoading && (error || isTileEmptyStatus(statusCode))) {
    return (
      <Box sx={{ width: '100%', height: '100%' }}>
        <TileErrorCard
          message={error}
          statusCode={statusCode}
          height={skeletonHeight(renderer)}
          title={heading}
          query={data?.meta?.query}
          registry={data?.meta?.registry}
          renderer={renderer}
          span={span}
        />
      </Box>
    );
  }

  if (isLoading || !data) {
    return (
      <Box sx={{ width: '100%', height: '100%' }}>
        <Skeleton
          variant="rounded"
          height={skeletonHeight(renderer)}
          sx={{ borderRadius: 2, width: '100%' }}
        />
      </Box>
    );
  }

  const displayData = hideTitle ? { ...data, display_name: undefined } : data;
  const Renderer = rendererMap[renderer];
  return (
    <Renderer
      config={config}
      data={displayData}
      span={span}
      presentation={presentation}
      metricId={id}
    />
  );
}
